import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check, Search, X } from "lucide-react";
import { ChevronDownIcon } from "../Dashboard/icons";
import {
  FONT_CATEGORIES,
  searchFonts,
  fontStackFor,
  loadFonts,
} from "../../utils/fonts";

export default function FontPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");
  const wrapRef = useRef(null);

  const results = useMemo(() => searchFonts(query, category), [query, category]);

  // The chosen family has to be loaded for the trigger — and the widget
  // preview beside it — to actually render in that face.
  useEffect(() => {
    loadFonts([value]);
  }, [value]);

  // Only the families currently on screen are fetched, so opening the list
  // doesn't pull eighty stylesheets at once.
  useEffect(() => {
    if (!open) return;
    loadFonts(results.slice(0, 40).map((font) => font.name));
  }, [open, results]);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      if (!wrapRef.current?.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const pick = (name) => {
    onChange(name);
    setOpen(false);
    setQuery("");
  };

  return (
    <div className="cz-font-picker" ref={wrapRef}>
      <button
        type="button"
        className="cz-font-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span style={{ fontFamily: fontStackFor(value) }}>{value}</span>
        <ChevronDownIcon />
      </button>

      {open && (
        <div className="cz-font-menu">
          <label className="cz-icon-search">
            <Search size={15} strokeWidth={1.9} />
            <input
              type="search"
              value={query}
              placeholder="Search fonts"
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search fonts"
              autoFocus
            />
            <button
              type="button"
              className="cz-icon-picker-close"
              onClick={() => setOpen(false)}
              aria-label="Close font picker"
            >
              <X size={16} strokeWidth={2} />
            </button>
          </label>

          <div className="cz-font-cats">
            {FONT_CATEGORIES.map((item) => (
              <button
                type="button"
                key={item}
                className={`cz-font-cat${category === item ? " is-active" : ""}`}
                onClick={() => setCategory(item)}
              >
                {item}
              </button>
            ))}
          </div>

          {results.length === 0 ? (
            <p className="cz-icon-empty">
              No fonts match &ldquo;{query.trim()}&rdquo;.
            </p>
          ) : (
            <ul className="cz-font-list" role="listbox">
              {results.map((font) => (
                <li key={font.name}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={font.name === value}
                    className={`cz-font-option${font.name === value ? " is-active" : ""}`}
                    onClick={() => pick(font.name)}
                  >
                    {/* Each row previews in its own face. */}
                    <span
                      className="cz-font-option__name"
                      style={{ fontFamily: fontStackFor(font.name) }}
                    >
                      {font.name}
                    </span>
                    <span className="cz-font-option__cat">{font.category}</span>
                    {font.name === value && (
                      <Check size={15} strokeWidth={2.6} />
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
